/**
 * useAuth — the primary hook for consuming authentication state.
 *
 * Returns:
 *  - state       — the full discriminated-union AuthState
 *  - isAuthenticated — convenience boolean
 *  - isLoading   — true while restoring / authenticating
 *  - session     — the active AuthSession, or null
 *  - logout()    — triggers a full logout + state reset
 *
 * Usage:
 *   const { isAuthenticated, session, logout } = useAuth()
 *
 * Must be used inside <AuthProvider>.
 *
 * Replaces:
 *  - useInitializationStatus (isLoggedIn / isInitialized)
 *  - usePartnerAuth (return value)
 *  - useAuthState (mixed concerns)
 */

import { useContext } from "react"

import { AuthContext } from "./auth-provider"
import type { AuthContextValue } from "./auth-types"

function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error(
      "useAuth must be used within an <AuthProvider>. " +
        "Wrap your app (or the relevant subtree) with <AuthProvider>."
    )
  }

  return context
}

export { useAuth }
